import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './UpdateSchoolForm.module.css';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import { updateSchoolSchema, superAdminUpdateSchoolSchema, UpdateSchoolFormValues } from '../../types/updateSchoolSchema';
import { api } from 'api/axios';
import { useNavigate, useParams } from 'react-router';
import Loading from 'components/Loading/Loading';
import editProfileIcon from 'assets/edit-profile.png';
import { useAuth } from 'hooks/auth';
import { UserRole } from 'types/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSchool,
    faEnvelope,
    faPhone,
    faUserTie,
    faGlobe,
    faUserShield,
    faUser,
    faSave,
    faCloudUploadAlt,
    faMapMarkerAlt,
    faLock
} from '@fortawesome/free-solid-svg-icons';
import {
    faFacebook,
    faLinkedin,
    faYoutube,
    faInstagram
} from '@fortawesome/free-brands-svg-icons';
import SocialMediaIcon from 'components/SocialMediaIcon/SocialMediaIcon';
import { countries } from 'utils/countries';

const UpdateSchoolForm: React.FC = () => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const { id } = useParams();
    const auth = useAuth();
    const userRole = auth?.user?.role;

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<any>({
        resolver: zodResolver(userRole === UserRole.SUPER_ADMIN ? superAdminUpdateSchoolSchema : updateSchoolSchema),
    });

    const facebookLink = watch('facebook_link');
    const instagramLink = watch('instagram_link');
    const linkedinLink = watch('linkedin_link');
    const youtubeLink = watch('youtube_link');
    const websiteUrl = watch('website_url');

    useEffect(() => {
        const fetchSchoolData = async () => {
            try {
                setIsLoading(true);
                const endpoint = id ? `/schools/${id}` : '/schools/my-school';
                const response = await api.get(endpoint);
                const school = response.data.data;

                // Map backend data to form fields
                reset({
                    schoolName: school.name,
                    schoolEmail: school.email,
                    schoolContact: school.contact,
                    address: school.address || "",
                    country: school.country || "",
                    principalName: school.principal_name,
                    principalEmail: school.principal_email,
                    principalContact: school.principal_contact,
                    website_url: school.website_url || "",
                    facebook_link: school.facebook_link || "",
                    linkedin_link: school.linkedin_link || "",
                    youtube_link: school.youtube_link || "",
                    instagram_link: school.instagram_link || "",
                    adminName: school.admin?.admin_name || "",
                    adminUsername: school.admin?.admin_username || "",
                    adminEmail: school.admin?.admin_email || "",
                    device_limit: school.device_limit !== null && school.device_limit !== undefined ? school.device_limit : "",
                    device_expiry: school.device_expiry ? (() => {
                        const d = new Date(school.device_expiry);
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    })() : "",
                });

                if (school.logo_url) {
                    setPreviewUrl(school.logo_url);
                }

                setError(null);
            } catch (err: any) {
                console.error('Error fetching school data:', err);
                setError(err.response?.data?.message || 'Failed to load school data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSchoolData();
    }, [reset]);

    const handleFileSelect = (file: File) => {
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setValue('logo', file, { shouldValidate: true });
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const handleCancel = () => {
        navigate(id ? '/school-management' : '/school-information');
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const onSubmit = async (data: any) => {
        try {
            console.log('Update School Data:', data);
            setIsUploading(true);

            let logoUrl = undefined;
            if (data.logo) {
                const { uploadImage } = await import('api/upload');
                logoUrl = await uploadImage(data.logo as File, 'schools');
            }

            const payload: any = {
                name: data.schoolName,
                email: data.schoolEmail,
                contact: data.schoolContact,
                address: data.address,
                country: data.country,
                principal_name: data.principalName,
                principal_email: data.principalEmail,
                principal_contact: data.principalContact,
                website_url: data.website_url,
                facebook_link: data.facebook_link,
                linkedin_link: data.linkedin_link,
                youtube_link: data.youtube_link,
                instagram_link: data.instagram_link,
            };

            if (userRole === UserRole.SUPER_ADMIN) {
                payload.device_limit = data.device_limit;
                payload.device_expiry = data.device_expiry;
            }

            if (logoUrl) {
                payload.logo_url = logoUrl;
            }

            const endpoint = id ? `/schools/${id}` : '/schools/my-school';
            await api.patch(endpoint, payload);
            alert('School information updated successfully!');
            navigate(id ? '/school-management' : '/school-information');
        } catch (err: any) {
            console.error('Error updating school:', err);
            const errorMessage = err.response?.data?.message || 'Failed to update school information';
            alert(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            {/* Breadcrumbs */}
            <div className={styles.breadcrumb}>
                <span className={styles.breadcrumbLink}>Settings</span>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span className={styles.breadcrumbCurrent}>Edit School Profile</span>
            </div>


            {isLoading ? (
                <Loading message="Loading school data..." />
            ) : error ? (
                <div className={styles.errorContainer}>{error}</div>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* School Identity Section */}
                    <div className={styles.identityCard}>
                        <div className={styles.identityContentRow}>
                            <div className={styles.logoCircle}>
                                {previewUrl ? (
                                    <img src={previewUrl} className={styles.logoPreview} />
                                ) : (
                                    <FontAwesomeIcon icon={faSchool} className={styles.placeholderIcon} />
                                )}
                            </div>
                            <div className={styles.identityTextCol}>
                                <h2 className={styles.identityTitle}>School Identity</h2>
                                <p className={styles.identitySubtitle}>
                                    Manage your institution's public branding and core administrative details.
                                </p>
                                <div
                                    className={styles.uploadBox}
                                    onClick={handleBrowseClick}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                                        onChange={handleFileInputChange}
                                    />
                                    <FontAwesomeIcon icon={faCloudUploadAlt} className={styles.uploadIcon} /> 
                                    <div className={styles.uploadTextBox}>
                                        <span className={styles.uploadBtnText}>Click to upload or drag and drop</span>
                                        <span className={styles.uploadHintText}>SVG, PNG, JPG or GIF (max. 800x800px)</span>
                                    </div>
                                </div>
                                {errors.logo && <p className={styles.errorMessage}>{errors.logo.message as string}</p>}
                            </div>
                        </div>
                    </div>

                    <div className={styles.formGrid}>
                        {/* LEFT COLUMN: School Basic Information & Principal */}
                        <div className={styles.leftCol}>
                            {/* Section 1: School Basic Information */}
                            <section className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <FontAwesomeIcon icon={faSchool} className={styles.cardIcon} />
                                    <h2 className={styles.cardTitle}>School Basic Information</h2>
                                </div>
                            <div className={styles.grid}>
                                <InputWithLabel
                                    label="School Name:"
                                    placeholder="Enter school name"
                                    icon={<FontAwesomeIcon icon={faSchool} />}
                                    error={errors.schoolName?.message as string}
                                    {...register('schoolName')}
                                />
                                <InputWithLabel
                                    label="School Email Address:"
                                    placeholder="Enter school email"
                                    icon={<FontAwesomeIcon icon={faEnvelope} />}
                                    error={errors.schoolEmail?.message as string}
                                    {...register('schoolEmail')}
                                />
                                <InputWithLabel
                                    label="School Contact Number:"
                                    placeholder="Enter school contact"
                                    icon={<FontAwesomeIcon icon={faPhone} />}
                                    error={errors.schoolContact?.message as string}
                                    {...register('schoolContact')}
                                />
                                <InputWithLabel
                                    label="School Address:"
                                    placeholder="Enter school address"
                                    icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
                                    error={errors.address?.message as string}
                                    {...register('address')}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className={styles.inputLabel} style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>Country:</label>
                                    <select 
                                        className={styles.inputField} 
                                        {...register('country')}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #D1D5DB', backgroundColor: '#FFFFFF', fontSize: '0.875rem', outline: 'none' }}
                                    >
                                        <option value="">Select a country...</option>
                                        {countries.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                    {errors.country && <span className={styles.errorMessage} style={{ color: '#EF4444', fontSize: '0.75rem' }}>{errors.country.message as string}</span>}
                                </div>
                            </div>
                            </section>

                            {/* Section 3: Principal Information */}
                            <section className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <FontAwesomeIcon icon={faUserTie} className={styles.cardIcon} />
                                    <h2 className={styles.cardTitle}>Principal Information</h2>
                                </div>
                                <div className={styles.grid}>
                                    <InputWithLabel
                                        label="Principal Name:"
                                        placeholder="Enter principal name"
                                        icon={<FontAwesomeIcon icon={faUserTie} />}
                                        error={errors.principalName?.message as string}
                                        {...register('principalName')}
                                    />
                                    <InputWithLabel
                                        label="Principal Email:"
                                        placeholder="Enter principal email"
                                        icon={<FontAwesomeIcon icon={faEnvelope} />}
                                        error={errors.principalEmail?.message as string}
                                        {...register('principalEmail')}
                                    />
                                    <InputWithLabel
                                        label="Principal Contact Number:"
                                        placeholder="Enter contact number"
                                        icon={<FontAwesomeIcon icon={faPhone} />}
                                        error={errors.principalContact?.message as string}
                                        {...register('principalContact')}
                                    />
                                </div>
                            </section>
                        </div>

                        {/* RIGHT COLUMN: Admin Contact & Social Media */}
                        <div className={styles.rightCol}>
                            {/* Section 2: School Administrator */}
                            {userRole === UserRole.SUPER_ADMIN && (
                                <section className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <FontAwesomeIcon icon={faUserShield} className={styles.cardIcon} />
                                        <h2 className={styles.cardTitle}>School Administrator</h2>
                                    </div>
                                    <div className={styles.grid}>
                                        <InputWithLabel
                                            label="Admin Name"
                                            placeholder="Full Name"
                                            disabled
                                            icon={<FontAwesomeIcon icon={faUserShield} />}
                                            error={errors.adminName?.message as string}
                                            {...register('adminName')}
                                        />
                                        <InputWithLabel
                                            label="Admin Username"
                                            placeholder="j.doe_admin"
                                            disabled
                                            icon={<FontAwesomeIcon icon={faUser} />}
                                            error={errors.adminUsername?.message as string}
                                            {...register('adminUsername')}
                                        />
                                        <InputWithLabel
                                            label="Admin Email"
                                            placeholder="admin.contact@email.com"
                                            disabled
                                            icon={<FontAwesomeIcon icon={faEnvelope} />}
                                            error={errors.adminEmail?.message as string}
                                            {...register('adminEmail')}
                                        />
                                    </div>
                                </section>
                            )}

                            {/* Section 4: Device Management */}
                            {userRole === UserRole.SUPER_ADMIN && (
                                <section className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <FontAwesomeIcon icon={faLock} className={styles.cardIcon} />
                                        <h2 className={styles.cardTitle}>Device Management</h2>
                                    </div>
                                    <div className={styles.grid}>
                                        <InputWithLabel
                                            label="Device Limit"
                                            type="number"
                                            placeholder="e.g. 50"
                                            icon={<FontAwesomeIcon icon={faLock} />}
                                            error={errors.device_limit?.message as string}
                                            {...register('device_limit')}
                                        />
                                        <InputWithLabel
                                            label="License Expiry Date"
                                            type="date"
                                            icon={<FontAwesomeIcon icon={faLock} />}
                                            error={errors.device_expiry?.message as string}
                                            {...register('device_expiry')}
                                        />
                                    </div>
                                </section>
                            )}

                            <section className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <FontAwesomeIcon icon={faGlobe} className={styles.cardIcon} />
                                    <h2 className={styles.cardTitle}>Social Media Information</h2>
                                </div>
                                <div className={styles.grid}>
                                    <div className={styles.socialInputGroup}>
                                        <InputWithLabel
                                            label="Website URL:"
                                            placeholder="https://example.com"
                                            icon={<FontAwesomeIcon icon={faGlobe} />}
                                            error={errors.website_url?.message as string}
                                            {...register('website_url')}
                                        />
                                    </div>
                                    <div className={styles.socialInputGroup}>
                                        <InputWithLabel
                                            label="Facebook Page:"
                                            placeholder="https://facebook.com/school"
                                            icon={<FontAwesomeIcon icon={faFacebook} />}
                                            error={errors.facebook_link?.message as string}
                                            {...register('facebook_link')}
                                        />
                                    </div>
                                    <div className={styles.socialInputGroup}>
                                        <InputWithLabel
                                            label="LinkedIn Page:"
                                            placeholder="https://linkedin.com/company/school"
                                            icon={<FontAwesomeIcon icon={faLinkedin} />}
                                            error={errors.linkedin_link?.message as string}
                                            {...register('linkedin_link')}
                                        />
                                    </div>
                                    <div className={styles.socialInputGroup}>
                                        <InputWithLabel
                                            label="YouTube Channel:"
                                            placeholder="https://youtube.com/c/school"
                                            icon={<FontAwesomeIcon icon={faYoutube} />}
                                            error={errors.youtube_link?.message as string}
                                            {...register('youtube_link')}
                                        />
                                    </div>
                                    <div className={styles.socialInputGroup}>
                                        <InputWithLabel
                                            label="Instagram Page:"
                                            placeholder="https://instagram.com/school"
                                            icon={<FontAwesomeIcon icon={faInstagram} />}
                                            error={errors.instagram_link?.message as string}
                                            {...register('instagram_link')}
                                        />
                                    </div>
                                </div>
                            </section>
                    </div>
                </div>
                <div className={styles.actions}>
                    <button type="button" className={styles.discardBtn} onClick={handleCancel}>
                        Discard
                    </button>
                    <button type="submit" className={styles.saveBtn} disabled={isSubmitting || isUploading}>
                        <FontAwesomeIcon icon={faSave} />
                        {isUploading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        )}
    </div>
    );
};

export default UpdateSchoolForm;
